// Continuing from where the code left off - completing the FAQ Accordion items
import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Nav, Tab } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  Code, 
  Settings, 
  Layers, 
  PenTool, 
  FileText, 
  Shield, 
  Database, 
  Server, 
  Monitor, 
  Smartphone, 
  BarChart, 
  Users, 
  Zap
} from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../styles/services/solutions/custom.css';

const CustomSoftwareSolutions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const industries = [
    {
      id: "healthcare",
      name: "Healthcare",
      description: "Custom electronic health record systems, patient management platforms, telehealth solutions, and medical billing software.",
      icon: <Shield size={40} />,
      features: ["HIPAA compliant systems", "Patient data management", "Medical appointment scheduling", "Healthcare analytics"]
    },
    {
      id: "finance",
      name: "Finance & Banking",
      description: "Secure financial transaction systems, investment management platforms, risk assessment software, and banking applications.",
      icon: <BarChart size={40} />,
      features: ["Regulatory compliance", "Fraud detection", "Investment analysis", "Financial reporting"]
    },
    {
      id: "retail",
      name: "Retail & E-commerce",
      description: "Inventory management systems, customer relationship management, point-of-sale solutions, and e-commerce platforms.",
      icon: <Smartphone size={40} />,
      features: ["Inventory tracking", "Customer analytics", "Payment processing", "Omnichannel integration"]
    },
    {
      id: "education",
      name: "Education",
      description: "Learning management systems, student information systems, remote education platforms, and assessment tools.",
      icon: <FileText size={40} />,
      features: ["Course management", "Student assessment", "Virtual classrooms", "Educational analytics"]
    },
    {
      id: "manufacturing",
      name: "Manufacturing",
      description: "Resource planning systems, production tracking software, quality control systems, and supply chain management.",
      icon: <Settings size={40} />,
      features: ["Production scheduling", "Inventory management", "Quality control", "Supply chain optimization"]
    },
    {
      id: "logistics",
      name: "Logistics & Transportation",
      description: "Fleet management systems, route optimization software, warehouse management, and shipment tracking applications.",
      icon: <Server size={40} />,
      features: ["Route optimization", "Fleet tracking", "Warehouse management", "Delivery management"]
    }
  ];

  return (
    <div className="custom-software-page">
      {/* Hero Section */}
      <motion.div 
        className="hero-section5 text-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <Container>
          <Row className="align-items-center min-vh-80">
            <Col lg={6} className="mb-5 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                <h1 className="display-3 fw-bold mb-4">Custom Software Solutions</h1>
                <p className="lead mb-4">Tailored software development to match your unique business model, streamline operations, and drive growth.</p>
                <Button variant="light" size="lg" className="me-3 mb-3">Discuss Your Project</Button>
                <Button variant="outline-light" size="lg" className="mb-3">View Our Work</Button>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                className="custom-software-animation"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                <div className="code-illustration-container">
                  <img 
                    src="/api/placeholder/600/400" 
                    alt="Custom Software Development Illustration" 
                    className="img-fluid rounded shadow-lg"
                  />
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </motion.div>

      {/* Services Section */}
      <section className="services-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Our Development Services</h2>
            <p className="lead text-muted">End-to-end software development tailored to your specific business needs</p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <Row className="g-4">
              {[
                { 
                  icon: <Code size={40} />, 
                  title: "Custom Application Development", 
                  description: "Bespoke software applications designed specifically for your business processes, workflows, and user needs." 
                },
                { 
                  icon: <Monitor size={40} />, 
                  title: "Web Application Development", 
                  description: "Responsive, feature-rich web applications with intuitive interfaces and robust backend functionality." 
                },
                { 
                  icon: <Smartphone size={40} />, 
                  title: "Mobile App Development", 
                  description: "Native and cross-platform mobile applications for iOS and Android that deliver exceptional user experiences." 
                },
                { 
                  icon: <Layers size={40} />, 
                  title: "API Development & Integration", 
                  description: "Custom API development and seamless integration with third-party services and existing systems." 
                },
                { 
                  icon: <Database size={40} />, 
                  title: "Database Design & Development", 
                  description: "Scalable, efficient database architecture optimized for your application's data requirements." 
                },
                { 
                  icon: <Shield size={40} />, 
                  title: "Security & Compliance Solutions", 
                  description: "Industry-standard security implementation with compliance for GDPR, HIPAA, PCI DSS, and other regulations." 
                }
              ].map((service, index) => (
                <Col md={6} lg={4} key={index}>
                  <motion.div
                    variants={fadeIn}
                  >
                    <Card className="service-card h-100 shadow border-0 hover-lift">
                      <Card.Body className="p-4">
                        <div className="service-icon mb-4 text-primary">
                          {service.icon}
                        </div>
                        <h4 className="fw-bold mb-3">{service.title}</h4>
                        <p className="text-muted mb-4">{service.description}</p>
                        <Button variant="outline-primary" className="stretched-link">Learn More</Button>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Process Section */}
      <section className="process-section py-5 bg-light">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Our Development Process</h2>
            <p className="lead text-muted">A systematic approach to delivering high-quality custom software</p>
          </motion.div>

          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="process-timeline">
                {[
                  {
                    icon: <Users size={32} />,
                    title: "Discovery & Analysis",
                    description: "We dive deep into your business processes, requirements, and challenges to understand exactly what your software needs to accomplish."
                  },
                  {
                    icon: <PenTool size={32} />,
                    title: "Design & Prototyping",
                    description: "Our designers create intuitive interfaces and user flows, then develop interactive prototypes for your feedback before coding begins."
                  },
                  {
                    icon: <Code size={32} />,
                    title: "Development & Testing",
                    description: "Our engineers build your solution using modern technologies and best practices, with rigorous testing at every stage."
                  },
                  {
                    icon: <Server size={32} />,
                    title: "Deployment & Integration",
                    description: "We carefully deploy your solution and ensure seamless integration with existing systems and third-party services."
                  },
                  {
                    icon: <Zap size={32} />,
                    title: "Support & Optimization",
                    description: "Post-launch, we provide ongoing support, maintenance, and continuous improvements to keep your software running optimally."
                  }
                ].map((step, index) => (
                  <motion.div 
                    key={index}
                    className="process-item"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2, duration: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <div className="process-icon">
                      {step.icon}
                    </div>
                    <div className="process-content">
                      <h4 className="fw-bold mb-3">{step.title}</h4>
                      <p className="text-muted">{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Industries Section */}
      <section className="industries-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Industry-Specific Solutions</h2>
            <p className="lead text-muted">Specialized software tailored to your industry's unique requirements</p>
          </motion.div>

          <Tab.Container id="industry-tabs" defaultActiveKey="healthcare">
            <Nav className="industry-nav mb-4 justify-content-center">
              {industries.map((industry) => (
                <Nav.Item key={industry.id}>
                  <Nav.Link eventKey={industry.id} className="industry-nav-link">
                    {industry.name}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
            <Tab.Content>
              {industries.map((industry) => (
                <Tab.Pane eventKey={industry.id} key={industry.id}>
                  <Row className="align-items-center">
                    <Col lg={6} className="mb-4 mb-lg-0">
                      <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                      >
                        <div className="industry-icon mb-4">
                          {industry.icon}
                        </div>
                        <h3 className="h2 fw-bold mb-3">{industry.name} Solutions</h3>
                        <p className="lead mb-4">{industry.description}</p>
                        <div className="industry-features">
                          {industry.features.map((feature, index) => (
                            <div key={index} className="industry-feature-item">
                              <div className="feature-bullet"></div>
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                        <Button variant="primary" className="mt-4">Discover {industry.name} Solutions</Button>
                      </motion.div>
                    </Col>
                    <Col lg={6}>
                      <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="industry-image-container"
                      >
                        <img 
                          src="/api/placeholder/600/400" 
                          alt={`${industry.name} Software Solution`} 
                          className="img-fluid rounded shadow-lg"
                        />
                      </motion.div>
                    </Col>
                  </Row>
                </Tab.Pane>
              ))}
            </Tab.Content>
          </Tab.Container>
        </Container>
      </section>

      {/* Customization Section */}
      <section className="customization-section py-5 bg-light">
        <Container>
          <Row className="align-items-center">
            <Col lg={5} className="mb-5 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="display-5 fw-bold mb-4">Tailored to Your Business</h2>
                <p className="lead mb-4">Your business is unique, and your software should be too. Our customization process ensures your solution perfectly fits your specific requirements.</p>
                
                <div className="customization-features">
                  {[
                    {
                      icon: <Settings size={24} />,
                      title: "Configurable Modules",
                      description: "Select and customize only the features your business needs."
                    },
                    {
                      icon: <Users size={24} />,
                      title: "User Role Management",
                      description: "Define custom permission levels and access controls for different user types."
                    },
                    {
                      icon: <PenTool size={24} />,
                      title: "Branded Interface",
                      description: "Customize the look and feel to match your company's branding."
                    },
                    {
                      icon: <Layers size={24} />,
                      title: "Workflow Automation",
                      description: "Create custom automation rules tailored to your business processes."
                    }
                  ].map((feature, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.2, duration: 0.6 }}
                      viewport={{ once: true }}
                      className="customization-item d-flex mb-4"
                    >
                      <div className="customization-icon me-3 text-primary">
                        {feature.icon}
                      </div>
                      <div className="customization-content">
                        <h5 className="fw-bold mb-2">{feature.title}</h5>
                        <p className="text-muted mb-0">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </Col>
            <Col lg={7}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="customization-builder shadow-lg">
                  <div className="builder-header">
                    <h4 className="mb-0">Software Customization Options</h4>
                  </div>
                  <div className="builder-body">
                    <Form>
                      <div className="builder-section mb-4">
                        <h5 className="builder-section-title">Core Functionality</h5>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Check 
                              type="checkbox" 
                              id="user-management" 
                              label="User Management" 
                              defaultChecked 
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Check 
                              type="checkbox" 
                              id="reporting" 
                              label="Advanced Reporting" 
                              defaultChecked 
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Check 
                              type="checkbox" 
                              id="workflows" 
                              label="Custom Workflows" 
                              defaultChecked 
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Check 
                              type="checkbox" 
                              id="integrations" 
                              label="Third-party Integrations" 
                            />
                          </Col>
                        </Row>
                      </div>
                      
                      <div className="builder-section mb-4">
                        <h5 className="builder-section-title">Interface Options</h5>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Check 
                              type="radio" 
                              name="interface-type" 
                              id="responsive-web" 
                              label="Responsive Web App" 
                              defaultChecked 
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Check 
                              type="radio" 
                              name="interface-type" 
                              id="mobile-app" 
                              label="Mobile Application" 
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Check 
                              type="radio" 
                              name="interface-type" 
                              id="desktop-app" 
                              label="Desktop Software" 
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Check 
                              type="radio" 
                              name="interface-type" 
                              id="multi-platform" 
                              label="Multi-platform Solution" 
                            />
                          </Col>
                        </Row>
                      </div>
                      
                      <div className="builder-section mb-4">
                        <h5 className="builder-section-title">Deployment Model</h5>
                        <Form.Select className="mb-3">
                          <option>Cloud-based SaaS Solution</option>
                          <option>On-premises Installation</option>
                          <option>Hybrid Deployment</option>
                          <option>Private Cloud Deployment</option>
                        </Form.Select>
                      </div>
                      
                      <div className="text-center">
                        <Button variant="primary" size="lg">Get Customized Quote</Button>
                      </div>
                    </Form>
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Case Studies Section */}
      <section className="case-studies-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Success Stories</h2>
            <p className="lead text-muted">See how our custom software solutions have transformed businesses</p>
          </motion.div>

          <Row className="g-4">
            {[
              {
                industry: "Healthcare",
                client: "MediTech Solutions",
                title: "Patient Management System",
                description: "Developed a comprehensive patient management system that reduced administrative workload by 40% and improved patient scheduling efficiency by 60%.",
                results: ["40% reduction in admin work", "60% improved scheduling", "98% user satisfaction rate"]
              },
              {
                industry: "Finance",
                client: "GlobalBank",
                title: "Automated Loan Processing System",
                description: "Created a custom loan application and processing system that reduced approval times from 5 days to just 6 hours while maintaining regulatory compliance.",
                results: ["80% faster loan processing", "35% increase in application volume", "99.9% system uptime"]
              },
              {
                industry: "Retail",
                client: "ShopSmart",
                title: "Integrated E-commerce Platform",
                description: "Built an omnichannel retail platform that unified online and in-store inventory, resulting in a 55% increase in online sales and improved customer satisfaction.",
                results: ["55% increase in online sales", "28% reduction in inventory costs", "42% higher customer retention"]
              }
            ].map((study, index) => (
              <Col lg={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Card className="case-study-card h-100 shadow border-0 hover-lift">
                    <div className="case-study-industry">{study.industry}</div>
                    <Card.Body className="p-4">
                      <p className="text-primary fw-bold mb-2">{study.client}</p>
                      <h4 className="fw-bold mb-3">{study.title}</h4>
                      <p className="text-muted mb-4">{study.description}</p>
                      <div className="case-study-results">
                        <h6 className="fw-bold mb-3">Key Results:</h6>
                        <ul className="results-list">
                          {study.results.map((result, i) => (
                            <li key={i}>{result}</li>
                          ))}
                        </ul>
                      </div>
                      <Button variant="outline-primary" className="mt-3">Read Full Case Study</Button>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
          <div className="text-center mt-5">
            <Button variant="primary" size="lg">View All Case Studies</Button>
          </div>
        </Container>
      </section>

      {/* Team Section */}
      <section className="team-section py-5 bg-light">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Meet Our Expert Team</h2>
            <p className="lead text-muted">Skilled professionals dedicated to bringing your software vision to life</p>
          </motion.div>

          <Row className="g-4">
            {[
              {
                name: "Alex Johnson",
                role: "Lead Software Architect",
                image: "/api/placeholder/300/300",
                description: "With 15+ years of experience in enterprise software architecture, Alex leads our technical design and ensures scalable, future-proof solutions."
              },
              {
                name: "Sarah Williams",
                role: "UX/UI Design Director",
                image: "/api/placeholder/300/300",
                description: "Sarah specializes in creating intuitive interfaces that balance beautiful design with functionality to deliver exceptional user experiences."
              },
              {
                name: "Michael Chen",
                role: "Lead Backend Developer",
                image: "/api/placeholder/300/300",
                description: "Michael's expertise in database architecture and API development ensures robust, high-performance systems that stand the test of time."
              },
              {
                name: "Priya Patel",
                role: "Project Management Lead",
                image: "/api/placeholder/300/300",
                description: "Priya's methodical approach to project management ensures on-time, on-budget delivery with clear communication every step of the way."
              }
            ].map((member, index) => (
              <Col md={6} lg={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Card className="team-card h-100 border-0 shadow-sm">
                    <div className="team-image-container">
                      <img src={member.image} alt={member.name} className="team-image" />
                    </div>
                    <Card.Body className="text-center p-4">
                      <h4 className="fw-bold mb-1">{member.name}</h4>
                      <p className="text-primary mb-3">{member.role}</p>
                      <p className="text-muted mb-3">{member.description}</p>
                      <div className="team-social">
                        <a href="/" className="team-social-link"><i className="bi bi-linkedin"></i></a>
                        <a href="/" className="team-social-link"><i className="bi bi-twitter"></i></a>
                        <a href="/" className="team-social-link"><i className="bi bi-envelope"></i></a>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5 text-white">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <motion.div
                className="text-center py-5"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="display-4 fw-bold mb-4">Ready to Transform Your Business?</h2>
                <p className="lead mb-5">Let's discuss how custom software can solve your business challenges and drive growth</p>
                <div className="cta-form bg-white p-4 rounded shadow-lg">
                  <Form className="row g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Control type="text" placeholder="Your Name" className="form-control-lg" />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Control type="email" placeholder="Your Email" className="form-control-lg" />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Control type="text" placeholder="Company" className="form-control-lg" />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Select className="form-control-lg">
                          <option>Select Your Industry</option>
                          <option>Healthcare</option>
                          <option>Finance & Banking</option>
                          <option>Retail & E-commerce</option>
                          <option>Education</option>
                          <option>Manufacturing</option>
                          <option>Logistics & Transportation</option>
                          <option>Other</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Control as="textarea" rows={3} placeholder="Tell us about your project" className="form-control-lg" />
                      </Form.Group>
                    </Col>
                    <Col md={12} className="text-center">
                      <Button variant="primary" size="lg" className="px-5">Schedule Free Consultation</Button>
                    </Col>
                  </Form>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="faq-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Frequently Asked Questions</h2>
            <p className="lead text-muted">Answers to common questions about our custom software development</p>
          </motion.div>

          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="accordion" id="faqAccordion">
                {[
                  {
                    question: "How long does it take to develop a custom software solution?",
                    answer: "The timeline for custom software development varies based on the complexity and scope of the project. Simple applications can take 2-3 months, while complex enterprise solutions may require 6-12 months or more. During our initial consultation, we'll provide a detailed timeline based on your specific requirements."
                  },
                  {
                    question: "What is the cost of developing custom software?",
                    answer: "Custom software costs vary widely depending on requirements, complexity, features, and integrations needed. We work with businesses of all sizes and can create solutions that fit different budget levels. After understanding your needs, we'll provide a detailed quote with transparent pricing."
                  },
                  {
                    question: "Will I own the source code of my custom software?",
                    answer: "Yes, our clients typically receive full ownership of the custom software we develop, including the source code. This gives you complete control over your application and the freedom to make future modifications or enhancements as needed."
                  },
                  {
                    question: "How do you ensure the security of custom software?",
                    answer: "Security is built into every phase of our development process. We implement industry best practices including secure coding standards, encryption, regular security testing, authentication mechanisms, and role-based access control. We also stay updated on emerging threats and compliance requirements specific to your industry."
                  },
                 // Continuing the FAQ section and completing the component
                 {
                    question: "Do you provide ongoing support after the software is deployed?",
                    answer: "Absolutely. We offer flexible maintenance and support plans to ensure your software remains secure, up-to-date, and functioning optimally. Our support packages include bug fixes, security updates, performance monitoring, and technical assistance. We can also implement new features and enhancements as your business evolves."
                  },
                  {
                    question: "Can you integrate my new software with existing systems?",
                    answer: "Yes, we specialize in creating seamless integrations between new software and your existing systems. Whether it's CRM, ERP, accounting software, or other business tools, we create secure, reliable connections that ensure data flows smoothly across your entire technology ecosystem."
                  },
                  {
                    question: "What technologies do you use for development?",
                    answer: "We use a wide range of modern technologies based on what's best for your specific project. Our developers are proficient in languages like JavaScript (React, Node.js, Angular), Python, .NET, Java, PHP, and mobile platforms like React Native and Swift. We select the technology stack that will best deliver your requirements while ensuring scalability and maintainability."
                  },
                  {
                    question: "How do you handle changes to requirements during development?",
                    answer: "We follow an agile development methodology that builds flexibility into the process. This means we can adapt to changing requirements while maintaining progress. When changes arise, we evaluate their impact on timeline and budget, discuss options with you, and implement approved changes in a controlled manner."
                  }
                ].map((faq, index) => (
                  <motion.div 
                    key={index}
                    className="accordion-item border-0 mb-3 shadow-sm"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <h3 className="accordion-header">
                      <button 
                        className="accordion-button fw-bold collapsed" 
                        type="button" 
                        data-bs-toggle="collapse" 
                        data-bs-target={`#faq-${index}`}
                        aria-expanded="false" 
                        aria-controls={`faq-${index}`}
                      >
                        {faq.question}
                      </button>
                    </h3>
                    <div 
                      id={`faq-${index}`} 
                      className="accordion-collapse collapse" 
                      data-bs-parent="#faqAccordion"
                    >
                      <div className="accordion-body">
                        <p className="mb-0">{faq.answer}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <motion.div
                className="text-center mt-5"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <p className="lead mb-4">Don't see your question here?</p>
                <Button variant="primary" size="lg">Contact Our Team</Button>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section py-5 bg-light">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">What Our Clients Say</h2>
            <p className="lead text-muted">Trusted by businesses of all sizes across industries</p>
          </motion.div>

          <Row className="g-4">
            {[
              {
                quote: "The custom CRM solution developed for our company has revolutionized how we manage our customer relationships. The team understood our unique requirements and delivered a system that perfectly fits our workflow.",
                author: "Jennifer Lee",
                position: "COO, Marketing Experts Inc.",
                image: "/api/placeholder/100/100"
              },
              {
                quote: "Working with this team was refreshingly different. They didn't just build what we asked for - they took the time to understand our business problems and proposed solutions we hadn't even considered.",
                author: "David Rodriguez",
                position: "CTO, FinTech Innovations",
                image: "/api/placeholder/100/100"
              },
              {
                quote: "The inventory management system they developed has eliminated our supply chain inefficiencies and given us real-time visibility into our stock levels across all locations. It's been a game-changer for our operations.",
                author: "Michael Thompson",
                position: "Operations Director, Retail Chain Co.",
                image: "/api/placeholder/100/100"
              }
            ].map((testimonial, index) => (
              <Col md={6} lg={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Card className="testimonial-card h-100 border-0 shadow p-4">
                    <div className="testimonial-quote mb-4">
                      <i className="bi bi-quote fs-1 text-primary opacity-25"></i>
                    </div>
                    <Card.Body className="p-0">
                      <p className="mb-4">{testimonial.quote}</p>
                      <div className="d-flex align-items-center">
                        <div className="testimonial-image me-3">
                          <img src={testimonial.image} alt={testimonial.author} className="rounded-circle" width="60" height="60" />
                        </div>
                        <div>
                          <h5 className="fw-bold mb-1">{testimonial.author}</h5>
                          <p className="text-muted mb-0">{testimonial.position}</p>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Technology Stack Section */}
      <section className="tech-stack-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Our Technology Stack</h2>
            <p className="lead text-muted">Modern technologies to build robust, scalable solutions</p>
          </motion.div>

          <Row className="g-4 justify-content-center">
            {[
              { category: "Frontend", technologies: ["React", "Angular", "Vue.js", "Next.js", "TypeScript"] },
              { category: "Backend", technologies: ["Node.js", "Python/Django", ".NET Core", "Java Spring", "PHP Laravel"] },
              { category: "Mobile", technologies: ["React Native", "Flutter", "iOS/Swift", "Android/Kotlin"] },
              { category: "Databases", technologies: ["PostgreSQL", "MongoDB", "MySQL", "Microsoft SQL", "Redis"] },
              { category: "DevOps", technologies: ["Docker", "Kubernetes", "AWS", "Azure", "CI/CD Pipelines"] },
              { category: "Testing", technologies: ["Jest", "Cypress", "Selenium", "JUnit", "Postman"] }
            ].map((stack, index) => (
              <Col md={6} lg={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Card className="tech-stack-card h-100 border-0 shadow-sm">
                    <Card.Body className="p-4">
                      <h4 className="tech-category fw-bold mb-3">{stack.category}</h4>
                      <div className="tech-list">
                        {stack.technologies.map((tech, i) => (
                          <span key={i} className="tech-item">{tech}</span>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="final-cta py-5 text-center text-white">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="py-5"
          >
            <h2 className="display-4 fw-bold mb-4">Start Your Digital Transformation Today</h2>
            <p className="lead mb-5 mx-auto" style={{ maxWidth: "700px" }}>
              Let's build the perfect software solution to take your business to the next level
            </p>
            <Button variant="light" size="lg" className="me-3 mb-3">Schedule Consultation</Button>
            <Button variant="outline-light" size="lg" className="mb-3">Browse Our Portfolio</Button>
            <p className="mt-4 text-white-50">No obligation consultation to discuss your project needs</p>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default CustomSoftwareSolutions;